import React, {useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {FaCheck, FaFileImport, FaUserPlus} from "react-icons/fa";
import {FaTriangleExclamation} from "react-icons/fa6";
import * as XLSX from "xlsx";
import Modal from "../../../components/modal";
import Button from "../../../components/button";
import TrainingsService from "../../../services/training-service";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import general from "../../../utils/general";
import {sortTrainingUsersByName} from "./training-users-utils";
import {toast} from "react-toastify";

const acceptedFileTypes = ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const normalizeCpf = (value) => String(value ?? "").replace(/\D/g, "");
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());
const formatPhone = (value) => {
    const number = String(value ?? "").replace(/\D/g, "");

    if (number.length === 10 || number.length === 11) {
        return number?.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3");
    }

    return value;
};

const getRequestErrorMessage = (error, fallback) => {
    return error?.response?.data?.message || error?.message || fallback;
};

const mapValidatedRows = (rows, importedRows) => {
    return rows.map((row) => {
        const result = importedRows.find((importedRow) => importedRow.cpf === row.normalizedCpf);

        if (!result) {
            return row;
        }

        return {
            ...row,
            cpf: general.formatCpf(result.cpf),
            email: result.email ?? row.email,
            errors: result.errors,
            missing_user: result.missing_user ?? false,
        };
    });
};

const parseCsvLine = (line) => {
    const values = [];
    let currentValue = "";
    let isInsideQuotes = false;

    for (let index = 0; index < line.length; index++) {
        const currentChar = line[index];
        const nextChar = line[index + 1];

        if (currentChar === '"') {
            if (isInsideQuotes && nextChar === '"') {
                currentValue += '"';
                index++;
            } else {
                isInsideQuotes = !isInsideQuotes;
            }
            continue;
        }

        if ((currentChar === ";" || currentChar === ",") && !isInsideQuotes) {
            values.push(currentValue.trim());
            currentValue = "";
            continue;
        }

        currentValue += currentChar;
    }

    values.push(currentValue.trim());

    return values;
};

const parseImportedUsers = (content) => {
    const rows = content
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

    if (rows.length === 0) {
        throw new Error("Arquivo vazio.");
    }

    const [headerLine, ...dataLines] = rows;
    const headers = parseCsvLine(headerLine).map(header => header.trim().toLowerCase());
    const nameIndex = headers.findIndex(header => header === "nome");
    const cpfIndex = headers.findIndex(header => header === "cpf");
    const emailIndex = headers.findIndex(header => header === "email");
    const phoneIndex = headers.findIndex(header => header === "telefone");
    const projectIndex = headers.findIndex(header => header === "projeto");
    const resaleIndex = headers.findIndex(header => header === "revenda");

    if (nameIndex === -1 || cpfIndex === -1 || emailIndex === -1 || phoneIndex === -1 || projectIndex === -1 || resaleIndex === -1) {
        throw new Error("O arquivo deve conter as colunas Nome, Cpf, Email, Telefone, Projeto e Revenda.");
    }

    return dataLines
        .map((line, index) => {
            const values = parseCsvLine(line);
            const name = values[nameIndex] ?? "";
            const cpf = values[cpfIndex] ?? "";
            const email = values[emailIndex] ?? "";
            const phone = values[phoneIndex] ?? "";
            const project = values[projectIndex] ?? "";
            const resale = values[resaleIndex] ?? "";
            const normalizedCpf = normalizeCpf(cpf);
            const trimmedEmail = email.trim();
            const errors = [];

            if (!name.trim()) {
                errors.push("Nome não informado");
            }

            if (normalizedCpf.length !== 11) {
                errors.push("CPF inválido");
            }

            if (trimmedEmail && !isValidEmail(trimmedEmail)) {
                errors.push("e-mail inválido");
            }

            return {
                id: `${normalizedCpf || "empty"}-${index}`,
                name: name.trim(),
                cpf: general.formatCpf(normalizedCpf || cpf),
                normalizedCpf,
                email: trimmedEmail,
                phone: phone.trim(),
                project: project.trim(),
                resale: resale.trim(),
                errors,
                isEmpty: !name.trim() && !cpf.trim() && !email.trim() && !phone.trim() && !project.trim() && !resale.trim(),
            };
        })
        .filter((row) => !row.isEmpty)
        .map(({isEmpty, ...row}) => row);
};

const parseImportedUsersFromWorksheetRows = (worksheetRows) => {
    if (worksheetRows.length === 0) {
        throw new Error("Arquivo vazio.");
    }

    return parseImportedUsers(
        worksheetRows
            .map((row) => row
                .map((value) => {
                    const normalizedValue = String(value ?? "");
                    return /[;,"\r\n]/.test(normalizedValue)
                        ? `"${normalizedValue.replace(/"/g, '""')}"`
                        : normalizedValue;
                })
                .join(";"))
            .join("\n")
    );
};

const buildImportedUsers = (validatedUsers) => {
    return validatedUsers.map((user) => ({
        id: null,
        user_id: user.id,
        name: user.user_name,
        cpf: user.cpf ? general.formatCpf(user.cpf) : null,
        email: user.email ?? null,
        project: user.project_name ?? null,
        resale: user.resale_name ?? null,
    }));
};

const buildImportPayload = (users) => {
    return users.map(({name, cpf, email, phone, project, resale}) => ({
        name,
        cpf,
        email,
        phone,
        project,
        resale,
    }));
};

const mergeImportedUsers = (selectedUsers, importedUsers) => {
    const usersById = new Map(selectedUsers.map((user) => [user.user_id, user]));

    importedUsers.forEach((user) => {
        usersById.set(user.user_id, user);
    });

    return sortTrainingUsersByName([...usersById.values()]);
};

const hasBlockingMissingUserError = (errors) => {
    return errors.some((error) => {
        const normalizedError = String(error).toLowerCase();

        return normalizedError.includes("email") || normalizedError.includes("e-mail") ||
            normalizedError.includes("cpf") || normalizedError.includes("nome");
    });
};

const escapeXml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const createWorksheetXml = (rows, textColumns = []) => {
    const xmlRows = rows.map((row, rowIndex) => {
        const cells = row.map((value, columnIndex) => {
            const column = String.fromCharCode(65 + columnIndex);
            const style = textColumns.includes(columnIndex) ? ' s="1"' : "";

            return `<c r="${column}${rowIndex + 1}"${style} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
        }).join("");

        return `<row r="${rowIndex + 1}">${cells}</row>`;
    }).join("");

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
        (textColumns.length > 0
            ? `<cols>${textColumns.map((columnIndex) => `<col min="${columnIndex + 1}" max="${columnIndex + 1}" width="15" customWidth="1" style="1"/>`).join("")}</cols>`
            : "") +
        `<sheetData>${xmlRows}</sheetData>` +
        `</worksheet>`;
};

const crc32Table = Array.from({length: 256}, (_, index) => {
    let crc = index;

    for (let bit = 0; bit < 8; bit++) {
        crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
    }

    return crc >>> 0;
});

const crc32 = (bytes) => {
    let crc = 0xFFFFFFFF;

    bytes.forEach((byte) => {
        crc = crc32Table[(crc ^ byte) & 0xFF] ^ (crc >>> 8);
    });

    return (crc ^ 0xFFFFFFFF) >>> 0;
};

const uint16 = (value) => [value & 0xFF, (value >>> 8) & 0xFF];
const uint32 = (value) => [value & 0xFF, (value >>> 8) & 0xFF, (value >>> 16) & 0xFF, (value >>> 24) & 0xFF];

const createXlsxBlob = (files) => {
    const encoder = new TextEncoder();
    const now = new Date();
    const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
    const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
    const parts = [];
    const centralDirectory = [];
    let offset = 0;

    files.forEach(({path, content}) => {
        const nameBytes = encoder.encode(path);
        const contentBytes = encoder.encode(content);
        const checksum = crc32(contentBytes);
        const localHeader = new Uint8Array([
            ...uint32(0x04034B50),
            ...uint16(20),
            ...uint16(0),
            ...uint16(0),
            ...uint16(dosTime),
            ...uint16(dosDate),
            ...uint32(checksum),
            ...uint32(contentBytes.length),
            ...uint32(contentBytes.length),
            ...uint16(nameBytes.length),
            ...uint16(0),
            ...nameBytes,
        ]);

        parts.push(localHeader, contentBytes);
        centralDirectory.push(new Uint8Array([
            ...uint32(0x02014B50),
            ...uint16(20),
            ...uint16(20),
            ...uint16(0),
            ...uint16(0),
            ...uint16(dosTime),
            ...uint16(dosDate),
            ...uint32(checksum),
            ...uint32(contentBytes.length),
            ...uint32(contentBytes.length),
            ...uint16(nameBytes.length),
            ...uint16(0),
            ...uint16(0),
            ...uint16(0),
            ...uint16(0),
            ...uint32(0),
            ...uint32(offset),
            ...nameBytes,
        ]));

        offset += localHeader.length + contentBytes.length;
    });

    const centralDirectoryOffset = offset;
    centralDirectory.forEach((entry) => {
        parts.push(entry);
        offset += entry.length;
    });

    parts.push(new Uint8Array([
        ...uint32(0x06054B50),
        ...uint16(0),
        ...uint16(0),
        ...uint16(files.length),
        ...uint16(files.length),
        ...uint32(offset - centralDirectoryOffset),
        ...uint32(centralDirectoryOffset),
        ...uint16(0),
    ]));

    return new Blob(parts, {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
};

const buildTemplateWorkbook = (projects, resales) => {
    const listRows = [["Projetos", "Revendas"]];
    const maxRows = Math.max(projects.length, resales.length);

    for (let index = 0; index < maxRows; index++) {
        listRows.push([projects[index] ?? "", resales[index] ?? ""]);
    }

    return createXlsxBlob([
        {
            path: "[Content_Types].xml",
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
                `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
                `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
                `<Default Extension="xml" ContentType="application/xml"/>` +
                `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
                `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
                `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
                `<Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
                `</Types>`,
        },
        {
            path: "_rels/.rels",
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
                `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
                `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
                `</Relationships>`,
        },
        {
            path: "xl/workbook.xml",
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
                `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
                `<sheets>` +
                `<sheet name="Importacao" sheetId="1" r:id="rId1"/>` +
                `<sheet name="Listas" sheetId="2" r:id="rId2"/>` +
                `</sheets>` +
                `</workbook>`,
        },
        {
            path: "xl/_rels/workbook.xml.rels",
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
                `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
                `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
                `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>` +
                `<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
                `</Relationships>`,
        },
        {
            path: "xl/styles.xml",
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
                `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
                `<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>` +
                `<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>` +
                `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
                `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
                `<cellXfs count="2">` +
                `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
                `<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>` +
                `</cellXfs>` +
                `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
                `</styleSheet>`,
        },
        {
            path: "xl/worksheets/sheet1.xml",
            content: createWorksheetXml([
                ["Nome", "Cpf", "Email", "Telefone", "Projeto", "Revenda"],
                ["", "", "", "", "", ""],
            ], [1]),
        },
        {
            path: "xl/worksheets/sheet2.xml",
            content: createWorksheetXml(listRows),
        },
    ]);
};

const ImportData = ({isOpen, onClose, selectedUsers, onConfirm, setIsLoading}) => {
    const fileInputRef = useRef();
    const gridRef = useRef();
    const [fileName, setFileName] = useState("");
    const [users, setUsers] = useState([]);
    const [isValidating, setIsValidating] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setFileName("");
            setUsers([]);
            setIsValidating(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (users.length > 0) {
            setTimeout(() => gridRef.current?.api?.sizeColumnsToFit(), 0);
        }
    }, [users]);

    const summary = useMemo(() => {
        return {
            total: users.length,
            valid: users.filter(user => user.errors.length === 0).length,
            invalid: users.filter(user => user.errors.length > 0).length,
            missing: users.filter(user => user.missing_user).length,
        };
    }, [users]);

    const canCreateMissingUsers = summary.missing > 0 && users.every((user) => (
        user.errors.length === 0 || (
            user.missing_user && !hasBlockingMissingUserError(user.errors)
        )
    ));

    const columnDefs = useMemo(() => ([
        {
            headerName: "",
            field: "errors",
            width: 56,
            sortable: false,
            filter: false,
            cellStyle: {justifyContent: "center"},
            cellRenderer: ({data}) => {
                if (data.missing_user) {
                    return (
                        <span
                            className="flex h-full w-full items-center justify-center"
                            title={data.errors.join("\n")}
                        >
                            <FaTriangleExclamation color="#f59e0b" />
                        </span>
                    );
                }

                if (data.errors.length > 0) {
                    return (
                        <span
                            className="flex h-full w-full items-center justify-center"
                            title={`${data.errors.join(" e ")}.`}
                        >
                            <FaTriangleExclamation color="red" />
                        </span>
                    );
                }

                return <span className="flex h-full w-full items-center justify-center"><FaCheck color="green" /></span>;
            },
        },
        {headerName: "Nome", field: "name", flex: 1},
        {headerName: "CPF", field: "cpf", flex: 1},
        {headerName: "Email", field: "email", flex: 1},
        {headerName: "Telefone", field: "phone", flex: 1, valueFormatter: ({value}) => formatPhone(value)},
        {headerName: "Projeto", field: "project", flex: 1},
        {headerName: "Revenda", field: "resale", flex: 1},
    ]), []);

    const validateImportedUsers = (rows) => {
        const pendingRows = rows.filter((row) => row.errors.length === 0);

        if (pendingRows.length === 0) {
            setUsers(rows);
            return;
        }

        setIsValidating(true);
        setIsLoading(true);

        TrainingsService.importUsers(buildImportPayload(pendingRows))
            .then(({data}) => {
                setUsers(mapValidatedRows(rows, data.rows));
            })
            .catch((error) => {
                setUsers([]);
                setFileName("");
                toast.error(getRequestErrorMessage(error, "Não foi possível validar os usuários importados."));
            })
            .finally(() => {
                setIsValidating(false);
                setIsLoading(false);
            });
    };

    const loadFile = (file) => {
        if (!file) {
            return;
        }

        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        if (!["csv", "xlsx"].includes(fileExtension)) {
            throw new Error("Somente arquivos CSV ou XLSX são aceitos.");
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                let importedRows;

                if (fileExtension === "xlsx") {
                    const workbook = XLSX.read(event.target?.result, {type: "array"});
                    const firstSheetName = workbook.SheetNames[0];

                    if (!firstSheetName) {
                        throw new Error("Arquivo vazio.");
                    }

                    importedRows = parseImportedUsersFromWorksheetRows(
                        XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {header: 1, defval: ""})
                    );
                } else {
                    importedRows = parseImportedUsers(event.target?.result ?? "");
                }

                setFileName(file.name);
                validateImportedUsers(importedRows);
            } catch (error) {
                setUsers([]);
                setFileName("");
                toast.error(error.message);
            }
        };
        reader.onerror = () => {
            toast.error("Não foi possível ler o arquivo.");
        };

        if (fileExtension === "xlsx") {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsText(file, "utf-8");
        }
    };

    const handleFileSelection = (event) => {
        try {
            loadFile(event.target.files?.[0]);
        } catch (error) {
            toast.error(error.message);
        } finally {
            event.target.value = "";
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            loadFile(event.dataTransfer.files?.[0]);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const confirm = () => {
        if (users.length === 0 || users.some((user) => user.errors.length > 0)) {
            return;
        }

        TrainingsService.importUsers(buildImportPayload(users))
            .then(({data}) => {
                onConfirm(mergeImportedUsers(selectedUsers, buildImportedUsers(data.users)));
                onClose();
            })
            .catch((error) => {
                toast.error(getRequestErrorMessage(error, "Não foi possível validar os usuários importados."));
            });
    };

    const createMissingUsers = () => {
        if (!canCreateMissingUsers) {
            return;
        }

        setIsValidating(true);
        setIsLoading(true);

        TrainingsService.importUsers(buildImportPayload(users), true)
            .then(({data}) => {
                setUsers(mapValidatedRows(users, data.rows));
                toast.success("Usuários cadastrados com sucesso.");
            })
            .catch((error) => {
                toast.error(getRequestErrorMessage(error, "Não foi possível cadastrar os usuários."));
            })
            .finally(() => {
                setIsValidating(false);
                setIsLoading(false);
            });
    };

    const clearSelection = () => {
        setUsers([]);
        setFileName("");
    };

    const downloadTemplate = async () => {
        setIsLoading(true);

        try {
            const {data} = await TrainingsService.importTemplateOptions();
            const blob = buildTemplateWorkbook(data.projects ?? [], data.resales ?? []);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = "Modelo de importacao Usuarios.xlsx";
            link.click();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(getRequestErrorMessage(error, "Não foi possível baixar o modelo de importação."));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Importar usuários" className="w-10/12 max-w-5xl">
            <div className="space-y-4 p-4">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedFileTypes}
                    className="hidden"
                    onChange={handleFileSelection}
                />

                {!fileName && (
                    <div
                        onDrop={handleDrop}
                        onDragOver={(event) => event.preventDefault()}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex h-[320px] cursor-pointer items-center justify-center rounded border border-dashed border-gray-400 bg-gray-50 text-center hover:bg-gray-100"
                    >
                        <div className="space-y-2 px-6">
                            <p className="text-lg font-medium text-gray-700">Clique aqui ou arraste o arquivo para fazer importação.</p>
                        </div>
                    </div>
                )}

                {fileName && (
                    <>
                        <div className="ag-theme-quartz h-[420px] w-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={users}
                                columnDefs={columnDefs}
                                defaultColDef={{sortable: true}}
                                suppressCellFocus
                                overlayNoRowsTemplate="Nenhuma linha importada"
                                gridOptions={{localeText: LocalizationAgGrid}}
                            />
                        </div>

                        <div className="flex gap-2 p-2 bg-[#efefef]">
                            <div className={"flex items-center justify-center"}>Total de usuários:
                                <span className={"ml-1 border border-[#555] w-[40px] h-[20px] font-bold text-[12px] flex items-center justify-center rounded-[3px]"}>{users.length}</span>
                            </div>
                            <div className={"flex items-center justify-center"}>
                                OK:
                                <span className={"ml-1 bg-[#008a0e] w-[40px] h-[20px] font-bold text-[12px] flex items-center justify-center rounded-[3px] text-white"}>
                                    {users.filter(u => u.errors.length === 0).length}
                                </span>
                            </div>
                            <div className={"flex items-center justify-center"}>Com problema:
                                <span className={"ml-1 bg-[#cb0000] w-[40px] h-[20px] text-white text-flex font-bold text-[12px] flex items-center justify-center rounded-[3px]"}>
                                    {users.filter(u => u.errors.length > 0).length}
                                </span>
                            </div>
                        </div>
                    </>
                )}

                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <Button type="button" target="success" onClick={downloadTemplate}>
                        <FaFileImport /> Baixar modelo
                    </Button>

                    <div className="flex gap-2">
                        <Button type="button" onClick={clearSelection} disabled={!fileName || isValidating}>
                            Limpar seleção
                        </Button>
                        <Button
                            type="button"
                            target="success"
                            onClick={createMissingUsers}
                            disabled={!canCreateMissingUsers || isValidating}
                        >
                            <FaUserPlus /> Cadastrar usuários
                        </Button>
                        <Button
                            type="button"
                            target="primary"
                            onClick={confirm}
                            disabled={users.length === 0 || summary.invalid > 0 || isValidating}
                        >
                            Confirmar importação
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImportData;
